import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { 
  TrendingUp, Award, Star, Users, Clock, DollarSign, 
  ChevronUp, ChevronDown, Calendar, CheckCircle,
  BarChart2, PieChart, Activity, Target
} from "lucide-react";
import { Card } from "../components/ui/card";
import SharedHeader4 from "../Headers/SharedHeader4";
import SharedFooter3 from "../Footer/SharedFooter3";
import { getProfessionalProgress } from "../redux/Actions/UserActions";
import { RootState, AppDispatch } from "../redux/store";
import { getDynamicCurrencySymbol } from "../lib/currencyUtils";

const ProgressDashboard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const { professionalProgress, professionalProgressLoading, professionalProgressError } = useSelector(
    (state: RootState) => state.user
  );
  const { user } = useSelector((state: any) => state.auth);
  
  // Get userId from URL params or use current user
  const userId = searchParams.get("userId") || user?.id;
  
  const [timeframe, setTimeframe] = useState("month");
  const [selectedMetric, setSelectedMetric] = useState("earnings");

  useEffect(() => {
    if (userId) {
      dispatch(getProfessionalProgress({ userId }));
    }
  }, [dispatch, userId]);

  if (professionalProgressLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t("progressPage.loading")}</p>
        </div>
      </div>
    );
  }

  if (professionalProgressError || !professionalProgress) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{professionalProgressError || t("progressPage.errorTitle")}</p>
          <button 
            onClick={() => dispatch(getProfessionalProgress({ userId }))} 
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            {t("progressPage.retry")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50"  style={{ textAlign: "left" }}>
      <SharedHeader4 />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{t("progressPage.title")}</h1>
          <p className="text-gray-600 mt-1">{t("progressPage.subtitle")}</p>
        </div>

        {/* Time Period Selector */}
        <div className="flex gap-2 mb-6">
          {["week", "month", "quarter", "year"].map((period) => (
            <button
              key={period}
              onClick={() => setTimeframe(period)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                timeframe === period
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              {t(`progressPage.timeframes.${period}`)}
            </button>
          ))}
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Earnings Card */}
          <Card 
            className={`p-6 cursor-pointer transition-all ${
              selectedMetric === "earnings" ? "ring-2 ring-blue-500" : ""
            }`}
            onClick={() => setSelectedMetric("earnings")}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <span className="text-xl font-bold text-green-600">{getDynamicCurrencySymbol()}</span>
              </div>
              <div className="flex items-center gap-1 text-sm">
                <span className={`flex items-center ${
                  professionalProgress.earnings.percentageChange >= 0 ? "text-green-600" : "text-red-600"
                }`}>
                  {professionalProgress.earnings.percentageChange >= 0 ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                  {Math.abs(professionalProgress.earnings.percentageChange)}%
                </span>
                <span className="text-gray-500">{t("progressPage.vsLast", { period: timeframe })}</span>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">
              {getDynamicCurrencySymbol()}{professionalProgress.earnings.current}
            </h3>
            <p className="text-sm text-gray-600 mt-1">{t("progressPage.totalEarnings")}</p>
          </Card>

          {/* Job Success Card */}
          <Card 
            className={`p-6 cursor-pointer transition-all ${
              selectedMetric === "jobSuccess" ? "ring-2 ring-blue-500" : ""
            }`}
            onClick={() => setSelectedMetric("jobSuccess")}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Star className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex items-center gap-1 text-sm">
                <span className="text-green-600 flex items-center">
                  <ChevronUp className="h-4 w-4" />
                  {professionalProgress.jobSuccess.percentageChange}%
                </span>
                <span className="text-gray-500">{t("progressPage.vsLast", { period: timeframe })}</span>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">
              {professionalProgress.jobSuccess.current}%
            </h3>
            <p className="text-sm text-gray-600 mt-1">{t("progressPage.jobSuccessRate")}</p>
          </Card>

          {/* Completion Rate Card */}
          <Card 
            className={`p-6 cursor-pointer transition-all ${
              selectedMetric === "completionRate" ? "ring-2 ring-blue-500" : ""
            }`}
            onClick={() => setSelectedMetric("completionRate")}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-purple-600" />
              </div>
              <div className="flex items-center gap-1 text-sm">
                <span className="text-green-600 flex items-center">
                  <ChevronUp className="h-4 w-4" />
                  {professionalProgress.completionRate.percentageChange}%
                </span>
                <span className="text-gray-500">{t("progressPage.vsLast", { period: timeframe })}</span>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">
              {professionalProgress.completionRate.current}%
            </h3>
            <p className="text-sm text-gray-600 mt-1">{t("progressPage.completionRate")}</p>
          </Card>

          {/* Response Rate Card */}
          <Card 
            className={`p-6 cursor-pointer transition-all ${
              selectedMetric === "responseRate" ? "ring-2 ring-blue-500" : ""
            }`}
            onClick={() => setSelectedMetric("responseRate")}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div className="flex items-center gap-1 text-sm">
                <span className="text-green-600 flex items-center">
                  <ChevronUp className="h-4 w-4" />
                  {professionalProgress.responseRate.percentageChange}%
                </span>
                <span className="text-gray-500">{t("progressPage.vsLast", { period: timeframe })}</span>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">
              {professionalProgress.responseRate.current}%
            </h3>
            <p className="text-sm text-gray-600 mt-1">{t("progressPage.responseRate")}</p>
          </Card>
        </div>

        {/* Skill Progress Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("progressPage.skillProgress")}</h3>
            <div className="space-y-4">
              {professionalProgress.skillProgress.map((skill) => (
                <div key={skill.skill}>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">{t(skill.skill)}</span>
                    <span className="text-sm text-gray-600">{skill.progress}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-600 rounded-full"
                      style={{ width: `${skill.progress}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Recent Milestones */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("progressPage.recentMilestones")}</h3>
            <div className="space-y-4">
              {professionalProgress.recentMilestones.map((milestone, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{milestone.title}</p>
                    <p className="text-sm text-gray-600">
                      {new Date(milestone.date).toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric"
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Goals Section */}
        <Card className="p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">{t("progressPage.currentGoals")}</h3>
            <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
              {t("progressPage.viewAllGoals")}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {professionalProgress.goals.map((goal, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">{t(goal.title)}</span>
                  <span className="text-sm text-gray-600">
                    {typeof goal.current === "number" && typeof goal.target === "number" 
                      ? `${goal.current.toLocaleString()} / ${goal.target.toLocaleString()}`
                      : `${goal.current} / ${goal.target}`
                    }
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${
                      index === 0 ? "bg-green-600" : 
                      index === 1 ? "bg-blue-600" : "bg-purple-600"
                    }`} 
                    style={{ width: `${goal.progress}%` }} 
                  />
                </div>
                <p className="text-sm text-gray-600">{t("progressPage.achieved", { progress: goal.progress })}</p>
              </div>
            ))}
          </div>
        </Card>
      </main>
      <br></br>
      <br></br>
      <br></br>
      <br></br>

      {/* Footer Actions - Mobile Only */}
      <SharedFooter3/>
    </div>
  );
};

export default ProgressDashboard;